import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Check, Zap, CalendarClock, Gauge, Plus, Copy } from "lucide-react";
import { api, apiError, money, formatDate, FEATURE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SOURCE_LABEL = { subscription: "Assinatura", admin_manual: "Manual (Admin)", promotion: "Promoção", courtesy: "Cortesia", trial: "Trial", migration: "Migração", system: "Padrão" };
const STATUS_LABEL = { active: "Ativo", pending: "Aguardando pagamento", overdue: "Vencido", delinquent: "Inadimplente", cancel_requested: "Cancelamento solicitado", cancelled: "Cancelado" };

export default function MyPlan() {
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState(null);
  const [extraBusy, setExtraBusy] = useState(false);
  const [subPix, setSubPix] = useState(null);
  const [needCpf, setNeedCpf] = useState(false);
  const [cpfInput, setCpfInput] = useState("");
  const [pendingPlan, setPendingPlan] = useState(null);

  const load = () => Promise.all([api.get("/me/plan"), api.get("/plans"), api.get("/subscriptions/me")])
    .then(([u, p, s]) => { setUsage(u.data); setPlans(p.data); setSubs(s.data); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail)))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const activeSub = subs.find((s) => ["active", "pending", "overdue"].includes(s.plan_status));

  const subscribe = async (slug) => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions", { plan: slug, billing_type: "PIX" });
      if (data.pix_copy_paste || data.pix_qr_image || data.invoice_url) {
        setSubPix({ id: data.id, plan: slug, value: data.value, plan_status: data.plan_status, pix_qr_image: data.pix_qr_image, pix_copy_paste: data.pix_copy_paste, invoice_url: data.invoice_url });
      } else {
        toast.success(data.message || "Assinatura criada.");
      }
      load();
    } catch (e) {
      const msg = apiError(e.response?.data?.detail);
      if (/cpf|cnpj/i.test(msg)) { setPendingPlan(slug); setCpfInput(""); setNeedCpf(true); }
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const submitCpf = async () => {
    const digits = (cpfInput || "").replace(/\D/g, "");
    if (digits.length !== 11 && digits.length !== 14) { toast.error("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido."); return; }
    setBusy(true);
    try {
      await api.put("/profile", { pix_holder_doc: digits });
      setNeedCpf(false);
      const slug = pendingPlan; setPendingPlan(null);
      await subscribe(slug);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const syncSub = async () => {
    if (!subPix) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${subPix.id}/sync`);
      if (data.plan_status === "active") { toast.success("Pagamento confirmado! Plano ativado 🎉"); setSubPix(null); load(); }
      else { toast.info("Pagamento ainda não identificado. Se já pagou, aguarde alguns segundos e tente novamente."); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancelSub = async (sid) => {
    setBusy(true);
    try { const { data } = await api.delete(`/subscriptions/${sid}`); toast.success(data.message || "Cancelamento solicitado."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const upgrade = () => document.getElementById("plans-grid")?.scrollIntoView({ behavior: "smooth" });

  const buyExtra = async () => {
    setExtraBusy(true);
    try { const { data } = await api.post("/events/extra/checkout"); setExtra(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setExtraBusy(false); }
  };
  const syncExtra = async () => {
    if (!extra) return;
    setExtraBusy(true);
    try {
      const { data } = await api.post(`/events/extra/${extra.id}/sync`);
      setExtra(data);
      if (data.collection_status === "PAID" || data.credited) { toast.success("Pagamento confirmado! +1 evento liberado."); setExtra(null); load(); }
      else toast.info("Pagamento ainda não identificado. Se já pagou, aguarde alguns segundos e tente de novo.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setExtraBusy(false); }
  };

  if (loading) return <div className="text-zinc-500">Carregando...</div>;
  if (!usage) return <div className="text-zinc-500">Não foi possível carregar seu plano.</div>;

  const cur = usage.plan;
  const pct = usage.unlimited ? 0 : Math.min(100, Math.round((usage.used / Math.max(usage.limit, 1)) * 100));

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="myplan-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3"><Crown className="h-7 w-7 text-[#FF4D00]" />Meu Plano</h1>
        <p className="text-sm text-muted-foreground mt-1">Acompanhe seu plano, consumo de eventos e benefícios.</p>
      </div>

      {/* Plano atual */}
      <div className="bg-[#121217] border border-border rounded-sm p-6 space-y-5" data-testid="current-plan-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-head text-2xl font-bold">{cur.name}</span>
              <Badge className="bg-[#FF4D00]/15 text-[#FF4D00] border-[#FF4D00]/30 rounded-sm">{STATUS_LABEL[usage.plan_status] || usage.plan_status}</Badge>
              <Badge className="bg-white/5 text-zinc-400 border-border rounded-sm">{SOURCE_LABEL[usage.plan_source] || usage.plan_source}</Badge>
            </div>
            <div className="text-zinc-500 text-sm mt-1">{cur.price > 0 ? `${money(cur.price)}/mês` : "Grátis"}</div>
          </div>
          {cur.order < 3 && (
            <Button data-testid="upgrade-btn" onClick={upgrade} className="bg-[#FF4D00] hover:bg-[#E64500] rounded-sm gap-2"><Zap className="h-4 w-4" />Fazer upgrade</Button>
          )}
        </div>

        {/* Consumo */}
        <div className="bg-[#09090B] border border-border rounded-sm p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="flex items-center gap-2 text-zinc-300"><Gauge className="h-4 w-4 text-[#00F0FF]" />{usage.lifetime ? "Evento gratuito (único)" : "Eventos criados neste ciclo"}</span>
            <span data-testid="usage-text" className="mono text-zinc-400">{usage.unlimited ? `${usage.used} · ilimitado` : `${usage.used} de ${usage.limit}`}</span>
          </div>
          {!usage.unlimited && (
            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-[#00F0FF]"}`} style={{ width: `${pct}%` }} />
            </div>
          )}
          <div className="flex items-center gap-2 text-[11px] text-zinc-600 mt-2"><CalendarClock className="h-3.5 w-3.5" />{usage.lifetime ? "Benefício único enquanto permanecer no plano Gratuito (não renova todo mês). Faça upgrade para criar mais eventos." : `Ciclo renova em ${formatDate(usage.cycle_end)}`}{usage.is_admin_unlimited ? " · conta administrativa (sem limite)" : ""}</div>
          {!usage.unlimited && usage.event_bonus_admin > 0 && (
            <div className="mt-2 text-[11px] text-success/90 flex items-center gap-1.5" data-testid="event-bonus-admin">
              <Plus className="h-3.5 w-3.5" />{usage.event_bonus_admin} evento(s) adicional(is) concedido(s) pelo administrador · disponível: {usage.remaining ?? 0} ({usage.plan_limit} do plano + {usage.event_bonus_admin} bônus − {usage.used} usados)
            </div>
          )}
          {!usage.unlimited && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              {usage.event_credits > 0 && <span data-testid="event-credits" className="text-xs px-2 py-1 rounded-sm bg-emerald-500/10 text-success border border-emerald-500/30">{usage.event_credits} evento(s) avulso(s) disponível(is)</span>}
              <Button data-testid="buy-extra-btn" onClick={buyExtra} disabled={extraBusy} size="sm" variant="outline" className="rounded-sm bg-transparent border-[#FF4D00]/40 text-[#FF4D00] gap-1 ml-auto"><Plus className="h-4 w-4" />Comprar evento avulso{usage.event_extra_fee ? ` (${money(usage.event_extra_fee)})` : ""}</Button>
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wide">Benefícios</div>
          <ul className="grid sm:grid-cols-2 gap-1.5">
            {(cur.features || []).map((ft) => (
              <li key={ft} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-success shrink-0" />{ft}</li>
            ))}
          </ul>
        </div>

        {(cur.entitlements || []).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wide">Recursos liberados</div>
            <div className="flex flex-wrap gap-1.5" data-testid="plan-entitlements">
              {cur.entitlements.map((k) => <span key={k} className="text-[11px] px-2 py-1 rounded-sm bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20">{FEATURE_LABELS[k] || k}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Assinatura em andamento */}
      {activeSub && (
        <div className="bg-[#121217] border border-[#00F0FF]/20 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="subscription-banner">
          <span className="text-sm">Assinatura <span className="font-semibold">{activeSub.plan}</span> · {money(activeSub.value)}/mês · <span className={activeSub.plan_status === "active" ? "text-success" : "text-amber-400"}>{STATUS_LABEL[activeSub.plan_status] || activeSub.plan_status}</span></span>
          <div className="flex items-center gap-2 ml-auto">
            {activeSub.plan_status !== "active" && (activeSub.invoice_url || activeSub.first_payment_id) && (
              <Button data-testid="view-charge-btn" onClick={() => setSubPix({ id: activeSub.asaas_subscription_id, plan: activeSub.plan, value: activeSub.value, plan_status: activeSub.plan_status, invoice_url: activeSub.invoice_url })} size="sm" variant="outline" className="rounded-sm bg-transparent border-[#FF4D00]/40 text-[#FF4D00]">Ver cobrança Pix</Button>
            )}
            <Button data-testid="cancel-sub-btn" onClick={() => cancelSub(activeSub.asaas_subscription_id)} disabled={busy} size="sm" variant="outline" className="rounded-sm bg-transparent border-red-500/30 text-destructive">Cancelar assinatura</Button>
          </div>
        </div>
      )}

      {/* Planos disponíveis */}
      <div id="plans-grid">
        <h2 className="font-head text-xl font-bold mb-3">Planos disponíveis</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map((p) => {
            const isCurrent = p.slug === cur.slug;
            return (
              <div key={p.slug} data-testid={`plan-card-${p.slug}`} className={`rounded-sm border p-4 flex flex-col ${isCurrent ? "border-[#FF4D00]/50 bg-[#FF4D00]/[0.04]" : "border-border bg-[#121217]"}`}>
                <div className="font-head font-bold text-lg">{p.name}</div>
                <div className="text-2xl font-bold mt-1">{p.price > 0 ? money(p.price) : "Grátis"}<span className="text-xs text-zinc-500 font-normal">{p.price > 0 ? "/mês" : ""}</span></div>
                <div className="text-xs text-zinc-500 mt-1">{p.slug === "gratuito" ? "1 evento (único)" : p.event_limit < 0 ? "Eventos ilimitados" : `${p.event_limit} evento(s)/mês`}</div>
                <ul className="mt-3 space-y-1 flex-1">
                  {(p.features || []).slice(0, 4).map((ft) => <li key={ft} className="flex items-start gap-1.5 text-[12px] text-zinc-400"><Check className="h-3.5 w-3.5 text-success shrink-0 mt-0.5" />{ft}</li>)}
                </ul>
                {isCurrent ? (
                  <div className="mt-3 text-center text-xs text-[#FF4D00] font-semibold py-2">Plano atual</div>
                ) : p.slug !== "gratuito" && !activeSub ? (
                  <Button data-testid={`choose-plan-${p.slug}`} onClick={() => subscribe(p.slug)} disabled={busy} size="sm" className="mt-3 rounded-sm bg-[#FF4D00] hover:bg-[#E64500]">Assinar</Button>
                ) : (
                  <div className="mt-3 text-center text-[11px] text-zinc-600 py-2">{activeSub ? "—" : "Plano gratuito"}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!extra} onOpenChange={(o) => { if (!o) setExtra(null); }}>        <DialogContent className="bg-[#121217] border-border text-white" data-testid="extra-pix-dialog">
          <DialogHeader><DialogTitle>Evento avulso — Pagamento Pix</DialogTitle></DialogHeader>
          {extra && (extra.collection_status === "PAID" || extra.credited ? (
            <p className="text-success text-sm py-4">Pagamento confirmado! +1 evento liberado.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Pague <span className="text-white font-semibold">{money(extra.total)}</span> via Pix para liberar <span className="text-white font-semibold">+1 evento</span>.</p>
              {extra.pix?.qr_code_base64 && <img src={`data:image/png;base64,${extra.pix.qr_code_base64}`} alt="QR Pix" className="w-48 h-48 mx-auto bg-white p-2 rounded-sm" />}
              {extra.pix?.copy_paste && (
                <div className="flex gap-2">
                  <input readOnly value={extra.pix.copy_paste} className="flex-1 bg-[#09090B] border border-border rounded-sm px-2 text-xs text-zinc-300" />
                  <Button size="sm" variant="outline" className="rounded-sm bg-transparent border-white/15" onClick={() => { navigator.clipboard.writeText(extra.pix.copy_paste); toast.success("Código Pix copiado."); }}><Copy className="h-4 w-4" /></Button>
                </div>
              )}
              <Button data-testid="extra-sync-btn" onClick={syncExtra} disabled={extraBusy} className="w-full rounded-sm bg-[#FF4D00] hover:bg-[#E64500]">Já paguei / Atualizar status</Button>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={needCpf} onOpenChange={(o) => { if (!o) { setNeedCpf(false); setPendingPlan(null); } }}>
        <DialogContent className="bg-[#121217] border-border text-white" data-testid="cpf-dialog">
          <DialogHeader><DialogTitle>Informe seu CPF ou CNPJ</DialogTitle><DialogDescription className="text-zinc-400">Necessário para gerar a cobrança do plano.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">Para gerar a cobrança do plano precisamos de um CPF ou CNPJ válido do titular. Ele fica salvo no seu perfil (Dados de recebimento).</p>
            <input data-testid="cpf-input" value={cpfInput} onChange={(e) => setCpfInput(e.target.value)} placeholder="CPF (11 dígitos) ou CNPJ (14 dígitos)" inputMode="numeric" className="w-full h-11 bg-[#09090B] border border-border rounded-sm px-3 text-sm text-white placeholder:text-zinc-600" />
            <Button data-testid="cpf-submit-btn" onClick={submitCpf} disabled={busy} className="w-full rounded-sm bg-[#FF4D00] hover:bg-[#E64500]">{busy ? "Processando..." : "Continuar para o pagamento"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!subPix} onOpenChange={(o) => { if (!o) setSubPix(null); }}>
        <DialogContent className="bg-[#121217] border-border text-white" data-testid="sub-pix-dialog">
          <DialogHeader><DialogTitle>Assinatura {subPix?.plan} — Pagamento Pix</DialogTitle><DialogDescription className="text-zinc-400">Escaneie o QR ou copie o código Pix para ativar seu plano.</DialogDescription></DialogHeader>
          {subPix && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Pague {subPix.value ? <span className="text-white font-semibold">{money(subPix.value)}</span> : "a cobrança"} via Pix para ativar seu plano. A ativação é automática após a confirmação.</p>
              {subPix.pix_qr_image && <img src={`data:image/png;base64,${subPix.pix_qr_image}`} alt="QR Pix" className="w-48 h-48 mx-auto bg-white p-2 rounded-sm" />}
              {subPix.pix_copy_paste && (
                <div className="flex gap-2">
                  <input readOnly value={subPix.pix_copy_paste} className="flex-1 bg-[#09090B] border border-border rounded-sm px-2 text-xs text-zinc-300" />
                  <Button size="sm" variant="outline" className="rounded-sm bg-transparent border-white/15" onClick={() => { navigator.clipboard.writeText(subPix.pix_copy_paste); toast.success("Código Pix copiado."); }}><Copy className="h-4 w-4" /></Button>
                </div>
              )}
              {subPix.invoice_url && (
                <a href={subPix.invoice_url} target="_blank" rel="noreferrer" data-testid="sub-invoice-link" className="block text-center text-sm text-[#FF4D00] underline">Abrir fatura / página de pagamento</a>
              )}
              {!subPix.pix_qr_image && !subPix.pix_copy_paste && !subPix.invoice_url && (
                <p className="text-sm text-amber-400">Assinatura criada. A cobrança Pix está sendo gerada — clique em atualizar em instantes.</p>
              )}
              <Button data-testid="sub-sync-btn" onClick={syncSub} disabled={busy} className="w-full rounded-sm bg-[#FF4D00] hover:bg-[#E64500]">Já paguei / Atualizar status</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
